// Current participant address, shared by Contact details and ILMP. Application
// payloads are historical fallbacks; this reader never changes its input records.
const PROVINCE_CODE_MAP = {
  ab: 'AB', alberta: 'AB', bc: 'BC', 'british columbia': 'BC',
  mb: 'MB', manitoba: 'MB', nb: 'NB', 'new brunswick': 'NB',
  nl: 'NL', 'newfoundland and labrador': 'NL', ns: 'NS', 'nova scotia': 'NS',
  nt: 'NT', 'northwest territories': 'NT', nu: 'NU', nunavut: 'NU',
  on: 'ON', ontario: 'ON', pe: 'PE', 'prince edward island': 'PE',
  qc: 'QC', quebec: 'QC', sk: 'SK', saskatchewan: 'SK',
  yt: 'YT', 'yukon territory': 'YT', us: 'US', usa: 'US', 'united states': 'US',
  other: 'OT', 'other country': 'OT',
};

const ADDRESS_FIELDS = {
  line1: ['line1', 'address1', 'address_line_1', 'street', 'street1', 'addressLine1', 'address'],
  line2: ['line2', 'address2', 'address_line_2', 'street2', 'addressLine2'],
  city: ['city', 'town', 'municipality'],
  province: ['province', 'province_code', 'region', 'state', 'territory'],
  postalCode: ['postalCode', 'postal_code', 'postcode', 'zip', 'zipcode'],
};

const ANSWER_FIELDS = {
  line1: ['address-street-address', 'address_street_address', 'home-address-line-1', 'home_address_line_1', 'home-address-line1', 'address-line-1', 'street-address'],
  line2: ['address-mailing-address', 'home-address-line-2', 'home_address_line_2', 'address-line-2'],
  city: ['address-city', 'address_city', 'home-address-city', 'home_address_city', 'city'],
  province: ['address-province', 'address_province', 'home-address-province', 'home_address_province', 'province', 'territory', 'state'],
  postalCode: ['address-postcode', 'address_postcode', 'home-address-postal-code', 'home_address_postal_code', 'postal-code', 'postal_code', 'postcode', 'zip'],
};

const object = value => value && typeof value === 'object' && !Array.isArray(value) ? value : {};
const owns = (value, key) => Object.prototype.hasOwnProperty.call(value, key);

function text(value) {
  if (typeof value === 'string') return value.trim() || null;
  if (typeof value === 'number') return String(value);
  if (Array.isArray(value)) return value.map(text).find(Boolean) || null;
  if (value && typeof value === 'object') return text(value.value ?? value.label ?? null);
  return null;
}

function structuredField(source, field) {
  if (typeof source === 'string') return field === 'line1' ? text(source) : null;
  return ADDRESS_FIELDS[field].map(key => text(object(source)[key])).find(Boolean) || null;
}

function answerField(answers, field) {
  const key = ANSWER_FIELDS[field].find(name => owns(answers, name));
  return key === undefined ? undefined : text(answers[key]);
}

function structuredAnswers(answers) {
  return ['address', 'home-address', 'home_address', 'residential-address', 'residential_address']
    .map(key => answers[key]);
}

function resolveParticipantAddress({ caseContext = {}, payload = {}, answers = {} } = {}) {
  const ctx = object(caseContext);
  const personal = object(ctx.applicationPersonal);
  const caseAnswers = object(ctx.applicationAnswers);
  const embeddedPayload = object(ctx.applicationPayload);
  const application = Object.assign({}, embeddedPayload, object(payload));
  const applicationPersonal = object(application.personal);
  const contact = object(application.contact);
  const applicationAnswers = Object.assign({}, object(embeddedPayload.answers), object(application.answers), object(answers));
  const currentSources = [
    ctx.address, ctx.homeAddress, personal.address, personal.home_address,
    personal.homeAddress, ...structuredAnswers(caseAnswers),
  ];
  const applicationSources = [
    applicationPersonal.address, applicationPersonal.home_address, applicationPersonal.homeAddress,
    contact.home_address, contact.homeAddress, contact.address, ...structuredAnswers(applicationAnswers),
    applicationPersonal,
  ];

  const result = {};
  for (const field of Object.keys(ADDRESS_FIELDS)) {
    const caseAnswer = answerField(caseAnswers, field);
    // The case save updates these answer fields as well as the address. An
    // explicitly cleared current answer must not revive a legacy address alias
    // or an old application value. Canonical saved values still take priority.
    const sources = caseAnswer === null ? [ctx.address, personal.address] : currentSources;
    const current = sources.map(source => structuredField(source, field)).find(Boolean);
    result[field] = current || (caseAnswer !== undefined ? caseAnswer : (
      applicationSources.map(source => structuredField(source, field)).find(Boolean) ||
      answerField(applicationAnswers, field) || null
    ));
  }
  if (result.province) {
    result.province = PROVINCE_CODE_MAP[result.province.toLowerCase()] || result.province.toUpperCase();
  }
  return result;
}

function formatParticipantStreetAddress(address) {
  if (!address?.line1) return null;
  return [address.line1, address.line2].filter(Boolean).join(', ');
}

module.exports = { PROVINCE_CODE_MAP, resolveParticipantAddress, formatParticipantStreetAddress };
